import { useLocation, useParams } from "react-router-dom";
import { getExperience, getProject, tagLabel } from "./queries";
import type { NodeType } from "./db";
import type { LensKey } from "../types";

export interface RouteContext {
  path: string;
  title: string;
  nodeType: NodeType;
  nodeId?: string;
  filters?: string;
  crumbs: { label: string; href?: string }[];
}

export function useRouteContext(): RouteContext {
  const location = useLocation();
  const params = useParams<{ lens?: string; id?: string }>();
  const search = new URLSearchParams(location.search);
  const path = location.pathname + (location.search || "");

  // Home
  if (location.pathname === "/") {
    return {
      path,
      title: "Home",
      nodeType: "home",
      crumbs: [{ label: "Home" }],
    };
  }

  // Lens views: /by/:lens
  if (location.pathname.startsWith("/by/") && params.lens) {
    const lens = params.lens as LensKey;
    const lensLabel = lensTitle(lens);
    const filterChips = filtersDescription(lens, search);
    const title = filterChips ? `${lensLabel}: ${filterChips}` : `By ${lensLabel}`;
    return {
      path,
      title,
      nodeType: "lens",
      nodeId: lens,
      filters: search.toString() || undefined,
      crumbs: [
        { label: "Home", href: "/" },
        { label: `By ${lensLabel}` },
        ...(filterChips ? [{ label: filterChips }] : []),
      ],
    };
  }

  // Experience: /experience/:id
  if (location.pathname.startsWith("/experience/") && params.id) {
    const exp = getExperience(params.id);
    const title = exp ? `${exp.company} — ${exp.role}` : params.id;
    return {
      path,
      title,
      nodeType: "experience",
      nodeId: params.id,
      crumbs: [
        { label: "Home", href: "/" },
        { label: "Experience" },
        { label: exp?.company ?? params.id },
      ],
    };
  }

  // Project: /project/:id
  if (location.pathname.startsWith("/project/") && params.id) {
    const proj = getProject(params.id);
    const title = proj?.title ?? params.id;
    const exp = proj ? getExperience(proj.experienceId) : undefined;
    return {
      path,
      title,
      nodeType: "project",
      nodeId: params.id,
      crumbs: [
        { label: "Home", href: "/" },
        ...(exp
          ? [{ label: exp.company, href: `/experience/${exp.id}` }]
          : [{ label: "Project" }]),
        { label: title },
      ],
    };
  }

  // Graph
  if (location.pathname.startsWith("/graph")) {
    return {
      path,
      title: "Connections graph",
      nodeType: "graph",
      crumbs: [{ label: "Home", href: "/" }, { label: "Graph" }],
    };
  }

  // Print
  if (location.pathname.startsWith("/print")) {
    return {
      path,
      title: "Print / PDF",
      nodeType: "print",
      crumbs: [{ label: "Home", href: "/" }, { label: "Print" }],
    };
  }

  return { path, title: location.pathname, nodeType: "home", crumbs: [{ label: "Home", href: "/" }] };
}

function lensTitle(lens: LensKey): string {
  switch (lens) {
    case "industry":
      return "Industry";
    case "technology":
      return "Technology";
    case "skill":
      return "Skill";
    case "role":
      return "Role";
    case "timeline":
      return "Timeline";
  }
}

function filtersDescription(lens: LensKey, search: URLSearchParams): string | undefined {
  const param =
    lens === "technology"
      ? "tech"
      : lens === "timeline"
        ? "era"
        : lens;
  const ids = search.getAll(param);
  if (!ids.length) return undefined;
  const labels = ids.map((id) => tagLabel(lens, id));
  return labels.join(" + ");
}
